import NiceModal, { useModal } from "@ebay/nice-modal-react"
import { Check, Loader2 } from "lucide-react"
import { useState } from "react"
import { useComputers } from "../../computers/context"
import { type AccessLevel, summarizeGrants } from "../../computers/people"
import { grantPersonAccess, principalMeta } from "../../computers/peopleAdmin"
import type { AuthPrincipalSummary } from "../../nest/generated"
import { PersonGlyph } from "../computers/PersonGlyph"
import { ProjectIcon } from "../sidebar/ProjectIcon"
import { Modal } from "../ui/Modal"
import { Segmented } from "./Segmented"
import { ACCESS_LEVELS_WITH_ADMIN, DURATIONS, durationExpiry, levelWord, untilLabel } from "./sharingCopy"

interface AccessSheetProps {
    nodeId: string
    principal: AuthPrincipalSummary
    projects: Array<{ id: string; name: string; color?: string; glyph?: string; projectChars?: string; projectEmoji?: string }>
}

/**
 * What one person may do here. A segmented level, the projects it applies
 * to, and how long. Taking everything away is a quiet action at the bottom,
 * separate from the choices above it.
 */
export const AccessSheet = NiceModal.create(({ nodeId, principal, projects }: AccessSheetProps) => {
    const modal = useModal()
    const computers = useComputers()
    const meta = principalMeta(principal)
    const current = summarizeGrants(principal.grants, meta.level === "propose")
    const [level, setLevel] = useState<AccessLevel>(current.level === "none" ? "propose" : current.level)
    const [selected, setSelected] = useState<Set<string>>(() => new Set(current.node ? projects.map((project) => project.id) : current.workspaceIds))
    const [duration, setDuration] = useState(DURATIONS[0]!.id)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const glyph = typeof meta.glyph === "string" ? meta.glyph : undefined
    const color = typeof meta.color === "string" ? meta.color : undefined
    const name = principal.displayName || "Colleague"

    const toggleProject = (id: string) => {
        setSelected((previous) => {
            const next = new Set(previous)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const apply = async (choice: AccessLevel | "none") => {
        setBusy(true)
        setError(null)
        try {
            const expiresAt = durationExpiry(duration)
            const workspaceIds = choice === "admin" ? [] : choice === "none" ? projects.map((project) => project.id) : [...selected]
            if (choice !== "admin" && choice !== "none" && workspaceIds.length === 0) throw new Error("Pick at least one project.")
            await grantPersonAccess(computers.backend, nodeId, principal, { level: choice, workspaceIds, ...(expiresAt ? { expiresAt } : {}) })
            modal.resolve()
            void modal.hide()
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
            setBusy(false)
        }
    }

    const currentSummary = current.node
        ? "Everything on this computer"
        : current.level === "none"
          ? "No access yet"
          : `${levelWord(current.level)} on ${current.workspaceIds.length} project${current.workspaceIds.length === 1 ? "" : "s"} ${untilLabel(current.expiresAt)}`.trim()

    return (
        <Modal
            title="Access"
            onClose={() => modal.hide()}
            size="md"
            footer={
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        className="btn flex h-11 flex-1 items-center justify-center gap-2 bg-primary text-sm font-medium text-primary-content disabled:opacity-50"
                        disabled={busy}
                        onClick={() => void apply(level)}
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        Apply
                    </button>
                    <button
                        type="button"
                        className="btn flex h-11 items-center justify-center px-3 text-sm text-error hover:bg-error/10 disabled:opacity-50"
                        disabled={busy}
                        onClick={() => void apply("none")}
                    >
                        Remove all access
                    </button>
                </div>
            }
        >
            <div className="flex flex-col gap-6">
                <div className="flex items-center gap-4">
                    <PersonGlyph name={name} glyph={glyph} color={color} size="xl" />
                    <div className="min-w-0">
                        <div className="truncate text-lg font-semibold tracking-tight text-base-content">{name}</div>
                        <div className="text-sm text-muted">{currentSummary}</div>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">Can</span>
                    <Segmented<AccessLevel> options={ACCESS_LEVELS_WITH_ADMIN} value={level} onChange={setLevel} label="Access level" />
                </div>

                {level !== "admin" && (
                    <div className="flex flex-col gap-2">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">On</span>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {projects.map((project) => {
                                const on = selected.has(project.id)
                                return (
                                    <button
                                        type="button"
                                        key={project.id}
                                        className={`btn flex min-h-11 items-center gap-2 border px-3 text-left text-sm ${
                                            on
                                                ? "border-primary bg-primary/5 text-base-content"
                                                : "border-border text-muted hover:bg-base-200 hover:text-base-content"
                                        }`}
                                        onClick={() => toggleProject(project.id)}
                                        aria-pressed={on}
                                    >
                                        <ProjectIcon
                                            projectId={project.id}
                                            projectName={project.name}
                                            color={project.color}
                                            glyph={project.glyph}
                                            projectChars={project.projectChars}
                                            projectEmoji={project.projectEmoji}
                                        />
                                        <span className="min-w-0 flex-1 truncate">{project.name}</span>
                                        {on && <Check className="h-3.5 w-3.5 flex-none text-primary" />}
                                    </button>
                                )
                            })}
                            {projects.length === 0 && <div className="col-span-full text-xs text-muted">This computer has no projects yet.</div>}
                        </div>
                    </div>
                )}

                <label className="flex flex-col gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">For</span>
                    <select
                        className="input h-11 w-full border border-border bg-input px-3 text-sm text-base-content outline-none focus:border-primary"
                        value={duration}
                        onChange={(event) => setDuration(event.target.value)}
                    >
                        {DURATIONS.map((candidate) => (
                            <option key={candidate.id} value={candidate.id}>
                                {candidate.label}
                            </option>
                        ))}
                    </select>
                </label>

                {error && (
                    <div role="alert" className="border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">
                        {error}
                    </div>
                )}
            </div>
        </Modal>
    )
})
